/**
 * New Cut — pick/type candidates, order, set cut line, save the whole ordered
 * list as an item pool with its cut line.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp, Scissors } from 'lucide-react';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { POOL_CREATE_LINE, useCreateRights } from '@/lib/authorization/createRights';
import { appTelemetry } from '@/lib/appTelemetry';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import { AppCard, Eyebrow, PrimaryButton, SecondaryButton } from '../components/primitives';
import { saveCutAsItemPool, type CutCandidate } from './cutActions';

type OptionSource = 'typed' | 'items';

export default function NewCutScreen() {
  const navigate = useNavigate();
  const app = getAppDefinition('cut')!;
  const userId = useUserStore((s) => s.userId);
  // Saving creates an item pool with no cycle or track (item_pools insert,
  // migration 754: POOL_SELECT), and each typed candidate becomes an item
  // (items insert, migration 751: items.create). The screen is drawn only for
  // a pool creator, and typing candidates only for an item creator too; picking
  // existing Items needs no item right (build contract §2.8).
  const createRights = useCreateRights();
  const mayTypeCandidates = createRights.items;
  const workspaceItems = useItemsStore((s) => s.items);
  // Opened straight from a link, nothing else has loaded the workspace's items
  // yet — "Pick from Items" would offer none.
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  const [poolName, setPoolName] = useState('');
  const [source, setSource] = useState<OptionSource>('typed');
  const [typedLines, setTypedLines] = useState('');
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [ordered, setOrdered] = useState<CutCandidate[] | null>(null);
  const [cutLine, setCutLine] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeSource: OptionSource = mayTypeCandidates ? source : 'items';

  const activeItems = useMemo(
    () => workspaceItems.filter((i) => !i.exit_status || i.exit_status === 'active'),
    [workspaceItems],
  );

  const draftCandidates: CutCandidate[] = useMemo(() => {
    if (activeSource === 'typed') {
      return typedLines
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        .map((title) => ({ title }));
    }
    return selectedItemIds.map((id) => {
      const item = activeItems.find((i) => i.id === id);
      return { id, title: item?.title ?? id };
    });
  }, [activeSource, typedLines, selectedItemIds, activeItems]);

  const working = ordered ?? draftCandidates;
  const keptCount = Math.min(cutLine, working.length);
  const canBuildOrder = draftCandidates.length >= 2;
  const canSave =
    Boolean(userId) &&
    poolName.trim().length > 0 &&
    working.length >= 2 &&
    cutLine >= 1 &&
    cutLine <= working.length &&
    !submitting;

  const move = (index: number, dir: -1 | 1) => {
    const list = [...working];
    const j = index + dir;
    if (j < 0 || j >= list.length) return;
    [list[index], list[j]] = [list[j], list[index]];
    setOrdered(list);
  };

  const handleSave = async () => {
    if (!userId || !canSave) return;
    setSubmitting(true);
    setError(null);
    const result = await saveCutAsItemPool({
      poolName,
      ordered: working,
      cutLine,
      ownerId: userId,
    });
    setSubmitting(false);
    if (!result.success || !result.poolId) {
      setError(result.error ?? 'Could not save the cut.');
      return;
    }
    void appTelemetry.setupCompleted('cut', {
      count: result.keptCount ?? 0,
    });
    navigate(`/prioritization/lists/${result.poolId}`, { replace: true });
  };

  if (!createRights.pool) {
    return (
      <AppShell app={app} backTo="/apps/cut" backLabel="Back to Cut">
        <ScreenHeading title="Draw a cut" />
        <p className="font-body text-sm text-gray-500 dark:text-[var(--text-ter)]" data-testid="cut-create-refused">
          {POOL_CREATE_LINE}
        </p>
      </AppShell>
    );
  }

  return (
    <AppShell app={app} backTo="/apps/cut" backLabel="Back to Cut">
      <ScreenHeading
        title="Draw a cut"
        subtitle="S2 — order the candidates and draw the cut. The whole list is saved as an item pool, with the cut line where you drew it."
      />
      <BallLine ballWith="you — the cut stays with your pool" />

      <div className="space-y-4">
        <AppCard>
          <Eyebrow>1 · Name the pool</Eyebrow>
          <input
            value={poolName}
            onChange={(e) => setPoolName(e.target.value)}
            placeholder="e.g. Q3 working set"
            className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 font-body text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-600 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
          />
        </AppCard>

        <AppCard>
          <Eyebrow>2 · Candidates</Eyebrow>
          <div className="mt-2 flex gap-2">
            {mayTypeCandidates ? (
              <SecondaryButton
                onClick={() => {
                  setSource('typed');
                  setOrdered(null);
                }}
                className={activeSource === 'typed' ? 'ring-2 ring-primary-600/40' : ''}
              >
                Type them in
              </SecondaryButton>
            ) : null}
            <SecondaryButton
              onClick={() => {
                setSource('items');
                setOrdered(null);
              }}
              className={activeSource === 'items' ? 'ring-2 ring-primary-600/40' : ''}
            >
              Pick from Items ({activeItems.length})
            </SecondaryButton>
          </div>
          {!mayTypeCandidates ? (
            <p className="mt-2 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]" data-testid="cut-typed-refused">
              Typing new candidates creates items, which takes the Create Items permission. Pick from Items instead.
            </p>
          ) : null}
          {activeSource === 'typed' ? (
            <textarea
              value={typedLines}
              onChange={(e) => {
                setTypedLines(e.target.value);
                setOrdered(null);
              }}
              rows={6}
              placeholder={'One candidate per line'}
              className="mt-3 w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 font-body text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-600 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
            />
          ) : (
            <div className="mt-3 max-h-56 space-y-1 overflow-y-auto">
              {activeItems.map((item) => {
                const checked = selectedItemIds.includes(item.id);
                return (
                  <label
                    key={item.id}
                    className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-gray-50 dark:hover:bg-[var(--hover-tint)]"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        setSelectedItemIds((prev) =>
                          checked ? prev.filter((id) => id !== item.id) : [...prev, item.id],
                        );
                        setOrdered(null);
                      }}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <span className="truncate font-body text-sm text-gray-800 dark:text-[var(--text-pri)]">
                      {item.title}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          <p className="mt-2 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
            {draftCandidates.length} candidate{draftCandidates.length === 1 ? '' : 's'} — need at least 2.
          </p>
        </AppCard>

        {canBuildOrder && (
          <AppCard>
            <Eyebrow>3 · Order & cut</Eyebrow>
            <p className="mt-1 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
              Top of the list is highest. Everything goes in the pool: kept items above the cut, cut items below it.
            </p>
            <ol className="mt-3 space-y-1">
              {working.map((c, idx) => {
                const above = idx < cutLine;
                return (
                  <li
                    key={`${c.id ?? c.title}-${idx}`}
                    className={`flex items-center gap-2 rounded-md border px-2 py-2 text-sm ${
                      above
                        ? 'border-alpine-green-200 bg-alpine-green-50/60 dark:border-alpine-green-800/40 dark:bg-alpine-green-900/15'
                        : 'border-gray-100 bg-gray-50/50 opacity-70 dark:border-[var(--rule)] dark:bg-[var(--surface)]'
                    }`}
                  >
                    <span className="w-5 tabular-nums text-gray-400">{idx + 1}</span>
                    <span className="min-w-0 flex-1 truncate text-gray-900 dark:text-[var(--text-pri)]">
                      {c.title}
                    </span>
                    <button
                      type="button"
                      onClick={() => move(idx, -1)}
                      className="rounded p-1 text-gray-500 hover:bg-white dark:hover:bg-[var(--hover-tint)]"
                      aria-label="Move up"
                    >
                      <ArrowUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(idx, 1)}
                      className="rounded p-1 text-gray-500 hover:bg-white dark:hover:bg-[var(--hover-tint)]"
                      aria-label="Move down"
                    >
                      <ArrowDown className="h-3.5 w-3.5" />
                    </button>
                  </li>
                );
              })}
            </ol>

            <div className="mt-4">
              <label className="flex items-center gap-2 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
                <Scissors className="h-4 w-4 text-slate-blue-600" />
                Keep top {keptCount} of {working.length}
              </label>
              <input
                type="range"
                min={1}
                max={working.length}
                value={cutLine}
                onChange={(e) => setCutLine(Number(e.target.value))}
                className="mt-2 w-full"
              />
            </div>
          </AppCard>
        )}

        {error ? (
          <p className="font-body text-sm text-luminous-coral-700 dark:text-luminous-coral-300">{error}</p>
        ) : null}

        <div className="flex justify-end">
          <PrimaryButton onClick={() => void handleSave()} disabled={!canSave}>
            {submitting ? 'Saving…' : `Save pool (${keptCount} kept, ${working.length - keptCount} cut)`}
          </PrimaryButton>
        </div>
      </div>
    </AppShell>
  );
}
